import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import CitySearchInput from "./CitySearchInput";
import ReactorsListModal from "./ReactorsListModal";
import SponsorModal from "./SponsorModal";
import { useIsMobile } from "../hooks/useIsMobile";

export type GalleryPhoto = {
  id: string; url: string; location?: string | null; likes_count: number; liked_by_me: boolean; media_type?: "image" | "video"; views_count?: number;
  // Renseignés uniquement pour un fil multi-auteurs (ex: le fil "Live" du Home) — sinon les
  // props ownerId/ownerName/ownerAvatar/isOwner du parent (une seule galerie, un seul auteur) priment.
  user_id?: string; author_name?: string; author_avatar?: string;
};

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const ChevronIcon = ({ dir }: { dir: "left" | "right" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    {dir === "left" ? <polyline points="15 18 9 12 15 6"/> : <polyline points="9 18 15 12 9 6"/>}
  </svg>
);
const ChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 0 1-8.4 8.4 8.3 8.3 0 0 1-3.8-.9L3 21l1.9-5.8a8.3 8.3 0 0 1-.9-3.8A8.4 8.4 0 0 1 12.5 3a8.4 8.4 0 0 1 8.4 8.4z"/>
  </svg>
);
const KebabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>
);
const LocationPinIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const EyeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
// Même coeur que le bouton "Top" des vibes (components/Stories.tsx) — plus le glyphe texte "★"
// utilisé avant ici, pour rester cohérent entre les deux fonctionnalités.
const HeartIcon = ({ filled, color }: { filled?: boolean; color: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/>
  </svg>
);

const initials = (name?: string) => (name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

// Visionneuse plein écran façon Instagram — l'image occupe tout l'espace, les actions (Top,
// Discuter, menu propriétaire) sont incrustées directement dessus sur des bandeaux dégradés,
// jamais dans un panneau séparé. Le tableau `photos` reste piloté par le parent : la visionneuse
// ne fait que lire l'index courant et notifier les changements en amont (like, suppression...).
export default function PhotoLightbox({
  photos, index, onIndexChange, onClose,
  isOwner, ownerId, ownerName, ownerAvatar, currentUserId,
  onLikeToggled, onDeleted, onLocationUpdated, onViewed,
}: {
  photos: GalleryPhoto[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  isOwner: boolean;
  ownerId: string;
  ownerName: string;
  ownerAvatar?: string;
  currentUserId?: string;
  onLikeToggled: (photoId: string, liked: boolean, likesCount: number) => void;
  onDeleted: (photoId: string) => void;
  onLocationUpdated: (photoId: string, location: string | null) => void;
  onViewed?: (photoId: string) => void;
}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [liking, setLiking] = useState(false);
  const [showLikers, setShowLikers] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  // Balayage tactile mobile — refs déclarées ici (avant le "return null" plus bas) : tous les
  // Hooks doivent s'exécuter dans le même ordre à chaque rendu, jamais après un retour anticipé.
  const touchStartXRef = useRef<number | null>(null);
  const touchMovedRef = useRef(false);


  useEffect(() => { setMenuOpen(false); setEditingLocation(false); setShowLikers(false); }, [index]);

  // Une vue par visionneur, uniquement pour les vidéos (galerie + "Live") — pas pour les photos,
  // où un compteur de vues n'aurait pas de sens comme sur les autres réseaux.
  useEffect(() => {
    const current = photos[index];
    if (current?.media_type === "video") {
      api.post(`/users/photos/${current.id}/view`, {}).then(data => { if (data?.success) onViewed?.(current.id); }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && index > 0) onIndexChange(index - 1);
      else if (e.key === "ArrowRight" && index < photos.length - 1) onIndexChange(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, onClose, onIndexChange]);

  const photo = photos[index];
  if (!photo) return null;

  const effOwnerId = photo.user_id || ownerId;
  const effOwnerName = photo.author_name || ownerName;
  const effOwnerAvatar = photo.author_avatar || ownerAvatar;
  const effIsOwner = photo.user_id ? photo.user_id === currentUserId : isOwner;

  const toggleLike = () => {
    if (liking) return;
    setLiking(true);
    api.post(`/users/photos/${photo.id}/like`, {}).then(data => {
      if (data && typeof data.liked === "boolean") onLikeToggled(photo.id, data.liked, data.likes_count);
    }).catch(() => {}).finally(() => setLiking(false));
  };

  const startEditLocation = () => { setLocationInput(photo.location || ""); setEditingLocation(true); setMenuOpen(false); };

  const saveLocation = () => {
    if (savingLocation) return;
    const loc = locationInput.trim() || null;
    setSavingLocation(true);
    api.put(`/users/${effOwnerId}/photos/${photo.id}`, { location: loc }).then(data => {
      // Ne jamais retomber sur la valeur locale tapée : si le serveur n'a pas confirmé
      // l'enregistrement (erreur, session expirée...), il faut le signaler plutôt que de
      // fermer la modale comme si tout s'était bien passé alors que rien n'a été persisté.
      if (data?.photo) {
        onLocationUpdated(photo.id, data.photo.location ?? null);
        setEditingLocation(false);
      } else {
        alert(data?.error || "Erreur lors de l'enregistrement du lieu");
      }
    }).catch(() => alert("Erreur de connexion")).finally(() => setSavingLocation(false));
  };

  const deletePhoto = () => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    api.delete(`/users/${effOwnerId}/photos/${photo.id}`).then(() => onDeleted(photo.id)).catch(() => {});
  };

  const startChat = () => {
    api.post(`/messages/direct/${effOwnerId}`, {}).then(data => {
      if (data?.conversationId) navigate(`/messages/${data.conversationId}`);
      else if (data?.error) alert(data.error);
    }).catch(() => {});
  };

  const navBtn: React.CSSProperties = { position: "fixed", top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", border: "none", backgroundColor: "rgba(255,255,255,0.14)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4003 };

  // Balayage tactile mobile — les chevrons de navigation sont volontairement masqués sur mobile
  // (voir !isMobile plus bas), le swipe est donc la SEULE façon d'y naviguer sans fermer et rouvrir
  // une autre photo depuis la galerie. touchMovedRef distingue un vrai balayage d'un simple tap
  // (qui doit toujours fermer la visionneuse via le clic sur le fond).
  const SWIPE_THRESHOLD = 50;
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchMovedRef.current = false;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current !== null && Math.abs(e.touches[0].clientX - touchStartXRef.current) > 10) {
      touchMovedRef.current = true;
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX === null) return;
    const delta = e.changedTouches[0].clientX - startX;
    if (delta > SWIPE_THRESHOLD && index > 0) onIndexChange(index - 1);
    else if (delta < -SWIPE_THRESHOLD && index < photos.length - 1) onIndexChange(index + 1);
  };

  // Rendu via portail directement sur document.body : le conteneur mobile de Layout.tsx utilise
  // WebkitOverflowScrolling:"touch", et sur Safari iOS un position:fixed descendant d'un tel
  // conteneur se positionne par rapport à TOUT son contenu scrollable plutôt qu'au vrai viewport
  // — l'image et la barre Top/Discuter se retrouvaient hors écran. Le portail échappe à cet ancêtre.
  return ReactDOM.createPortal(
    <div
      onClick={() => { if (!touchMovedRef.current) onClose(); }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ position: "fixed", inset: 0, backgroundColor: "black", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", touchAction: "pan-y" }}
    >
      {photo.media_type === "video" ? (
        <video src={photo.url} controls autoPlay onClick={e => e.stopPropagation()} style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }} />
      ) : (
        <img src={photo.url} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }} />
      )}

      {/* Bandeau haut — auteur, lieu, menu propriétaire, fermer */}
      <div onClick={e => e.stopPropagation()} style={{ position: "fixed", top: 0, left: 0, right: 0, padding: "16px 16px", background: "linear-gradient(to bottom, rgba(0,0,0,0.65), transparent)", display: "flex", alignItems: "center", gap: 10, zIndex: 4002 }}>
        <div onClick={() => !effIsOwner && navigate(`/profile/${effOwnerId}`)} style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#333", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, flexShrink: 0, cursor: effIsOwner ? "default" : "pointer" }}>
          {effOwnerAvatar ? <img src={effOwnerAvatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(effOwnerName)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: "white", textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>{effOwnerName}</span>
            {photo.media_type === "video" && (
              <span style={{ fontSize: 10.5, fontWeight: 800, color: "white", backgroundColor: "#e74c3c", borderRadius: 6, padding: "2px 6px", letterSpacing: 0.5 }}>(live)</span>
            )}
            {photo.media_type === "video" && !!photo.views_count && (
              effIsOwner ? (
                <button onClick={e => { e.stopPropagation(); setShowViewers(true); }} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11.5, color: "rgba(255,255,255,0.85)", border: "none", background: "none", cursor: "pointer", padding: 0 }}>
                  <EyeIcon /> {photo.views_count}
                </button>
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11.5, color: "rgba(255,255,255,0.85)" }}>
                  <EyeIcon /> {photo.views_count}
                </span>
              )
            )}
          </div>
          {photo.location && (
            <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11.5, color: "rgba(255,255,255,0.85)" }}>
              <LocationPinIcon /> {photo.location}
            </div>
          )}
        </div>
        {effIsOwner && (
          <div style={{ position: "relative" }}>
            <button onClick={() => setMenuOpen(v => !v)} style={{ border: "none", background: "none", cursor: "pointer", padding: 6, display: "flex" }}>
              <KebabIcon />
            </button>
            {menuOpen && (
              <>
                <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 10 }} />
                <div style={{ position: "absolute", top: "120%", right: 0, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.3)", zIndex: 20, minWidth: 170 }}>
                  <button onClick={startEditLocation} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>
                    {photo.location ? "Modifier le lieu" : "Ajouter un lieu"}
                  </button>
                  {photo.media_type === "video" && (
                    <button onClick={() => { setMenuOpen(false); setShowSponsorModal(true); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a", borderTop: "1px solid #F5F5F5" }}>
                      Booster ce Live
                    </button>
                  )}
                  <button onClick={deletePhoto} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#c0392b", borderTop: "1px solid #F5F5F5" }}>
                    Supprimer
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", padding: 6, display: "flex" }}>
          <CloseIcon />
        </button>
      </div>

      {editingLocation && (
        <div onClick={e => e.stopPropagation()} style={{ position: "fixed", top: 66, left: 16, right: 16, maxWidth: 360, backgroundColor: "white", borderRadius: 12, padding: 14, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", zIndex: 4002 }}>
          <div style={{ marginBottom: 8 }}>
            <CitySearchInput
              city={locationInput}
              onChangeText={setLocationInput}
              onSelect={label => setLocationInput(label)}
              placeholder="Ex : Bandol, Var ou Milan"
              inputStyle={{ padding: "9px 11px", fontSize: 13 }}
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={saveLocation} disabled={savingLocation} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12.5, fontWeight: 600, cursor: savingLocation ? "default" : "pointer", opacity: savingLocation ? 0.6 : 1 }}>{savingLocation ? "Enregistrement..." : "Enregistrer"}</button>
            <button onClick={() => setEditingLocation(false)} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#666", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      {/* Bandeau bas — Top et Discuter, incrustés sur l'image comme sur Instagram. Bouton "Top"
          masqué pour le/la propriétaire (liker son propre contenu n'a pas de sens) plutôt que
          désactivé-mais-visible — le compteur de likes reste affiché séparément juste après. */}
      <div onClick={e => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "20px 16px", background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)", display: "flex", alignItems: "center", gap: 10, zIndex: 4002 }}>
        {!effIsOwner && (
          <button onClick={toggleLike} style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: photo.liked_by_me ? "white" : "rgba(255,255,255,0.16)", borderRadius: 20, padding: "9px 16px", cursor: "pointer" }}>
            <HeartIcon filled={photo.liked_by_me} color={photo.liked_by_me ? "#0a0a0a" : "white"} />
            <span style={{ fontSize: 13, fontWeight: 700, color: photo.liked_by_me ? "#0a0a0a" : "white" }}>Top</span>
          </button>
        )}
        {photo.likes_count > 0 && (
          <button onClick={e => { e.stopPropagation(); setShowLikers(true); }} style={{ border: "none", background: "rgba(255,255,255,0.16)", borderRadius: 20, padding: "9px 14px", cursor: "pointer", color: "white", fontSize: 13, fontWeight: 700 }}>
            {photo.likes_count}
          </button>
        )}
        {!effIsOwner && (
          <button onClick={startChat} style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "rgba(255,255,255,0.16)", borderRadius: 20, padding: "9px 16px", cursor: "pointer" }}>
            <ChatIcon />
            <span style={{ fontSize: 13, fontWeight: 700, color: "white" }}>Discuter</span>
          </button>
        )}
      </div>

      {!isMobile && index > 0 && (
        <button onClick={e => { e.stopPropagation(); onIndexChange(index - 1); }} style={{ ...navBtn, left: 16 }}><ChevronIcon dir="left" /></button>
      )}
      {!isMobile && index < photos.length - 1 && (
        <button onClick={e => { e.stopPropagation(); onIndexChange(index + 1); }} style={{ ...navBtn, right: 16 }}><ChevronIcon dir="right" /></button>
      )}

      {showLikers && (
        <ReactorsListModal
          title={`Top · ${photo.likes_count}`}
          load={() => api.get(`/users/photos/${photo.id}/likers`).then(d => d?.likers || [])}
          onClose={() => setShowLikers(false)}
        />
      )}

      {showViewers && (
        <ReactorsListModal
          title={`Vu par ${photo.views_count || 0}`}
          load={() => api.get(`/users/photos/${photo.id}/viewers`).then(d => d?.viewers || [])}
          onClose={() => setShowViewers(false)}
          showLikedBadge
        />
      )}

      {showSponsorModal && (
        <SponsorModal targetType="live" targetId={photo.id} targetLabel="Ton Live" onClose={() => setShowSponsorModal(false)} />
      )}
    </div>,
    document.body
  );
}
